"use client";

import ResponsiveImage from "@/components/Shared/ResponsiveImage";
import { SliderItemProps } from "@/data/Static/sliderdata";
import Link from "next/link";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";



interface SliderItemComponentProps {
  item: SliderItemProps;
  className?: string;
  isPriority?: boolean;
  isActive?: boolean;
}

export default function HeroSliderItem({
  item,
  className = "",
  isPriority = false,
  isActive = false,
}: SliderItemComponentProps) {

  return (
    <section className={`relative overflow-hidden w-full h-full ${className}`}>

      {/* Background Image with Ken Burns Effect */}
      <div className="absolute inset-0 z-0">
        <motion.div
          initial={{ scale: 1.1, opacity: 0 }}
          animate={isActive ? { scale: 1, opacity: 1 } : { scale: 1.1, opacity: 0 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
          className="relative w-full h-full"
        >
          <ResponsiveImage
            src={`/images/hero/${item.image}.${item.extension}`}
            alt={item.imageAlt ?? item.highlightText}
            fill
            priority={isPriority}
            loading={isPriority ? "eager" : "lazy"}
            className="object-cover object-[35%_center] 2xl:object-[30%_center]"
            style={item.imagePosition ? { objectPosition: item.imagePosition } : undefined}
            sizes="100vw"
          />
          {/* Sophisticated Overlay Gradient */}
          <div className="absolute inset-0 bg-linear-to-r from-hill-primary/80 via-hill-primary/40 to-transparent z-10" />
          <div className="absolute inset-0 bg-linear-to-b from-black/20 via-transparent to-hill-primary/40 z-10" />
        </motion.div>
      </div>

      <div className="
  relative container mx-auto h-full px-6 z-20
  flex flex-col lg:flex-row
  items-center
  justify-center
  xl:justify-start
  xl:pt-[10vh]
">
        
        {/* Text Content - Staggered Slide In */}
        <div className="max-w-2xl text-center lg:text-left pt-12 lg:pt-0">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isActive ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-serif font-bold text-white mb-6 leading-[1.1] tracking-tight">
              {item.highlightText}
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isActive ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            <p className="text-lg md:text-xl lg:text-2xl font-karla text-white/90 mb-10 leading-relaxed font-light italic border-l-4 border-hill-secondary pl-6 inline-block text-left">
              {item.description}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isActive ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
            transition={{ delay: 0.7, duration: 0.5 }}
          >
            {item.link && (
              <Link 
                href={item.link}
                className="inline-flex items-center gap-3 px-8 py-4 bg-hill-secondary-strong hover:bg-hill-secondary text-white rounded-full font-bold text-lg transition-all hover:shadow-2xl hover:-translate-y-1 active:scale-95 group"
              >
                Tovább olvasok
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            )}
          </motion.div>
        </div>

        {/* Featured Image with Premium Frame */}
        <motion.div
          initial={{ opacity: 0, x: 40, rotate: 2 }}
          animate={isActive ? { opacity: 1, x: 0, rotate: 0 } : { opacity: 0, x: 40, rotate: 2 }}
          transition={{ delay: 0.6, duration: 1, ease: "easeOut" }}
          className="relative hidden md:block shrink-0"
        >
          <div className="relative p-3 z-10 transition-transform duration-500 hover:scale-105 active:scale-100 cursor-pointer">
            <div className="relative w-[350px] md:w-[450px] lg:w-[550px] h-[250px] md:h-[350px] lg:h-[450px] rounded-3xl overflow-hidden shadow-2xl border-4 border-white/10">
              <ResponsiveImage
                src={`/images/hero/${item.image}.${item.extension}`}
                alt={item.imageAlt ?? item.highlightText}
                fill
                priority={isPriority}
                loading={isPriority ? "eager" : "lazy"}
                className="object-cover"
                style={item.imagePosition ? { objectPosition: item.imagePosition } : undefined}
                sizes="(min-width: 1024px) 550px, (min-width: 768px) 450px, 350px"
              />
              <div className="absolute inset-0 bg-hill-primary/10 group-hover:bg-transparent transition-colors duration-500" />
            </div>
            
            {/* Elegant Accents */}
            <div className="absolute -bottom-6 -left-6 w-32 h-32 border-b-[8px] border-l-[8px] border-hill-secondary/80 rounded-bl-3xl -z-1" />
            <div className="absolute -top-6 -right-6 w-32 h-32 border-t-[8px] border-r-[8px] border-hill-secondary/80 rounded-tr-3xl -z-1" />
          </div>
        </motion.div>

      </div>
    </section>
  );
}
